/* Heatmap screen: canvas stage, inspector, legend. Colours come from the
   terminal's CSS variables (terminal.css). */

.root {
  min-width: 0;
  border: 1px solid var(--line);
  background: var(--paper-raised);
}

.head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--line-strong);
  background: var(--head-fill);
}

.title {
  margin: 0;
  color: var(--signal);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.subtitle {
  color: var(--ink-muted);
  font-size: 0.68rem;
}

.controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--line);
}

.pick {
  display: flex;
  min-width: 0;
  flex: 0 1 260px;
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.root button,
.root select {
  min-height: 32px;
  padding: 4px 10px;
  border: 1px solid var(--line-strong);
  background: #0d0d0d;
  color: var(--ink);
  font: inherit;
  font-size: 0.74rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.root select {
  width: 100%;
  min-width: 0;
  color: var(--signal);
  text-transform: none;
}

.root button {
  cursor: pointer;
}

.root button:hover:not(:disabled) {
  border-color: var(--signal);
  color: var(--signal);
}

.root button[aria-pressed="true"] {
  border-color: var(--signal);
  background: var(--signal-fill);
  color: var(--on-signal);
}

.root button:disabled {
  cursor: default;
  opacity: 0.4;
}

.body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 240px;
}

.stage {
  position: relative;
  min-width: 0;
  overflow: hidden;
  background: var(--paper);
}

.canvas {
  display: block;
  width: 100%;
  height: clamp(420px, 64vh, 760px);
  touch-action: none;
  cursor: grab;
}

.canvas:active {
  cursor: grabbing;
}

.canvas:focus-visible {
  outline: 1px solid var(--signal);
  outline-offset: -1px;
}

.tooltip {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: min(260px, calc(100% - 8px));
  padding: 6px 9px;
  border: 1px solid var(--signal);
  background: rgba(0, 0, 0, 0.92);
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}

.tooltip[hidden] {
  display: none;
}

.tooltip strong {
  color: var(--signal);
}

.tipName {
  overflow: hidden;
  color: var(--ink-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flat {
  color: var(--ink-muted);
}

.hint {
  position: absolute;
  right: 10px;
  bottom: 8px;
  left: 10px;
  margin: 0;
  color: var(--ink-muted);
  font-size: 0.64rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  pointer-events: none;
}

.details {
  min-width: 0;
  padding: 12px;
  border-left: 1px solid var(--line);
}

.eyebrow {
  display: flex;
  justify-content: space-between;
  margin: 0;
  color: var(--signal);
  font-size: 0.66rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.eyebrow span {
  color: var(--ink-muted);
}

.symbol {
  margin: 10px 0 2px;
  overflow-wrap: anywhere;
  color: var(--signal);
  font-size: 1.6rem;
}

.name {
  margin: 0 0 10px;
  color: var(--ink-muted);
  font-size: 0.74rem;
}

.shareTrack {
  height: 3px;
  background: var(--line);
}

.shareTrack span {
  display: block;
  height: 100%;
  min-width: 1px;
  background: var(--signal);
}

.details dl {
  display: grid;
  gap: 10px;
  margin: 12px 0 0;
}

.details dt {
  color: var(--ink-muted);
  font-size: 0.64rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.details dd {
  margin: 2px 0 0;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
}

.details dd.small {
  font-size: 0.74rem;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 18px;
  padding: 8px 10px;
  border-top: 1px solid var(--line);
  color: var(--ink-muted);
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
}

.legendItem {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ramp {
  width: 96px;
  height: 7px;
  background: linear-gradient(90deg, var(--down), var(--ink-muted), var(--up));
}

.swatch {
  flex: none;
  width: 12px;
  height: 7px;
  background: var(--line-strong);
}

.empty {
  margin: 0;
  padding: 32px 12px;
  color: var(--ink-muted);
}

@media (max-width: 760px) {
  .body {
    grid-template-columns: minmax(0, 1fr);
  }

  .canvas {
    height: min(380px, 70vh);
  }

  .details {
    border-top: 1px solid var(--line);
    border-left: 0;
  }

  .details dl {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .details .wide {
    grid-column: 1 / -1;
  }

  .pick {
    flex: 1 1 100%;
  }
}
